/** @format */
/** istanbul ignore file */

import { ActivatedRoute, Data, NavigationEnd, Params, Router, RouterModule } from '@angular/router';
import {
  CartService,
  CookieService,
  HeaderLocationService,
  KillswitchService,
  Store,
  StoreService,
  UtilityService,
  VirtualHostCookieService,
  WINDOW,
} from '@shared/data-access/services';
import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  PLATFORM_ID,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import {
  CompareBarProduct,
  CompareProductDetails,
  ProductAvailabilityCollection,
} from '@shared/data-access/interfaces';
import { NEVER, Observable, firstValueFrom, of, switchMap } from 'rxjs';
import { ProductListCompareService, ProductListMlService } from '@plp/data-access/services';
import {
  changeHeaderServiceStoreInfo,
  selectPageType,
  setCommonData,
  setCommonDsgVhHeader,
  setUserStoreInfoFromCookie,
  setUserStoreName,
  setUserZipCode,
} from '@state/data-access';
import { filter, map, mergeMap, take } from 'rxjs/operators';
import {
  productListLoadTriggered,
  selectFeatureFlags,
  selectProductListCore,
  selectSearchTerm,
} from '@plp/data-access/state';
import {
  selectCompareBarProducts,
  selectCompareProductDetails,
  selectCompareProductDetailsFailed,
  selectCompareProductsAvailability,
  selectCompareSkeletonLoader,
  selectIsCompareGridOpen,
} from '@plp/data-access/compare';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { AuthenticationService } from '@shared/authentication/data-access';
import { CeopsComponent } from '@shared/ceops';
import { CertonaService } from '@product-recommendations/data-access';
import { CompareGridComponent } from '@compare/components';
import { HmfDrawerComponent } from '@dsg-tech/homefield-angular';
import { MetaService } from '@seo/data-access';
import { Store as NgRxStore } from '@ngrx/store';
import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { ProductList } from '@plp/data-access/interfaces';
import { ProductListSnackbarComponent } from '@plp/components';
import { analyticsSortMethodValues } from '@plp/data-access/constants';
import { chain } from '@core/chain';
import { isPlatformBrowser } from '@angular/common';

declare const BUILD_VERSION: string;

const IMPORTS = [
  CeopsComponent,
  CompareGridComponent,
  HmfDrawerComponent,
  ProductListSnackbarComponent,
  RouterModule,
];
@Component({
  selector: 'plp-srlp-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [...IMPORTS],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:HeaderServices:SameDayDelivery:OnLoad)': 'setDeliveryZipOnLoad($event)',
    '(window:HeaderServices:SameDayDelivery:Change)': 'setDeliveryZipOnChange($event)',
    '(window:HeaderServices:StoreSelection:Change)': 'updateStoreDetails($event)',
  },
})
export class AppComponent {
  readonly drawerComponent = viewChild(HmfDrawerComponent);

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly analyticsService = inject(ProductAnalyticsService);
  private readonly authService = inject(AuthenticationService);
  private readonly cartService = inject(CartService);
  private readonly cookieService = inject(CookieService);
  private readonly document = inject(DOCUMENT);
  private readonly headerLocationService = inject(HeaderLocationService);
  private readonly killSwitchService = inject(KillswitchService);
  private readonly metaService = inject(MetaService);
  private readonly ngrxStore = inject(NgRxStore);
  private readonly plpMlService = inject(ProductListMlService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly storeService = inject(StoreService);
  private readonly utilityService = inject(UtilityService);
  private readonly virtualHostCookieService = inject(VirtualHostCookieService);
  private readonly window = inject<Window>(WINDOW);
  private readonly platformId = inject(PLATFORM_ID);
  protected readonly plpCompareService = inject(ProductListCompareService);
  private readonly certonaService = inject(CertonaService);

  readonly featureFlags = this.ngrxStore.selectSignal(selectFeatureFlags);
  readonly isCompareGridOpen = this.ngrxStore.selectSignal(selectIsCompareGridOpen);
  readonly compareProductDetails = this.ngrxStore.selectSignal<CompareProductDetails | null>(
    selectCompareProductDetails,
  );

  readonly compareBarProducts =
    this.ngrxStore.selectSignal<CompareBarProduct[]>(selectCompareBarProducts);

  readonly compareProductsAvailability = this.ngrxStore.selectSignal<ProductAvailabilityCollection>(
    selectCompareProductsAvailability,
  );

  readonly isProductDataLoading = this.ngrxStore.selectSignal<boolean>(selectCompareSkeletonLoader);

  readonly hasApiFailed = this.ngrxStore.selectSignal<boolean>(selectCompareProductDetailsFailed);
  readonly pageType = this.ngrxStore.selectSignal(selectPageType);
  private readonly productListCore = this.ngrxStore.selectSignal(selectProductListCore);
  private readonly searchTerm = this.ngrxStore.selectSignal(selectSearchTerm);

  // We want to wait for the client-side product list data before firing analytics
  private readonly isProductListReady = computed(() => {
    const productListCore = this.productListCore();
    const productList = productListCore?.productList;
    return !!productList && !productList.fromTransferState;
  });

  private readonly currentRouteSignal = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      mergeMap((route: ActivatedRoute) =>
        of({
          data: route.data,
          params: route.params,
          queryParams: route.queryParams,
        }),
      ),
    ),
  );

  private readonly queryParams = toSignal(
    toObservable(this.currentRouteSignal).pipe(switchMap((route) => route?.queryParams ?? NEVER)),
  );

  readonly drawerSize = this.plpCompareService.drawerSize;
  readonly drawerDirection = this.plpCompareService.drawerDirection;

  private readonly canProcessAnalytics = signal(false);
  private readonly sddZipCode = signal<string | null>('');
  private readonly hasDrawerBeenInitialized = signal(false);

  readonly compareCheckboxEnabled = computed(() => this.featureFlags()?.compareCheckboxEnabled);
  readonly isDrawerOpen = computed(
    () => this.compareCheckboxEnabled() && this.plpCompareService.shouldDrawerBeOpen(),
  );

  private readonly storeCookieValue = this.store.selectDistinctKeySignal('CUST_STORE');

  constructor() {
    // Check authentication status and set up authentication status stream
    this.authService.localAuthSetup();

    // set dsg_vh in the common state
    this.ngrxStore.dispatch(setCommonData());
    this.ngrxStore.dispatch(setCommonDsgVhHeader({ dsgVh: this.cookieService.get('DSG_VH') }));

    // Reset the analytics
    if (!this.window.siteData) {
      this.analyticsService.resetAnalytics();
    }

    // Check to see if this has been defined
    if (typeof BUILD_VERSION === 'string' && BUILD_VERSION.trim() !== '') {
      // Set the build version on the global window
      this.window.BUILD_VERSION = BUILD_VERSION;
    }

    /*
     * Only execute on browser
     */
    if (isPlatformBrowser(this.platformId)) {
      // eliminate 'persistent' click focus
      document.body.addEventListener('mousedown', () => {
        document.body.classList.add('mouse-Event');
      });

      document.body.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        document.body.classList.remove('mouse-Event');
      });

      // Initialize the Cart Service
      this.cartService.initialize();

      // Check cookie and populate Store
      this.authService.checkIfReturningCustomer();

      // Set default store
      this.storeService.initialize();

      /*
       * DSG_UID cookie
       */
      if (!this.cookieService.check('DSG_UID')) {
        // Create a DSG_UID cookie
        this.cookieService.set('DSG_UID', this.utilityService.generateUUID(), 365, '/');
      }

      // Virtual Host chain identifier
      this.virtualHostCookieService.setVirtualHostCookie();

      // Set the user zip code from the cookie
      this.setUserZipCodeFromCookie();
    }

    effect(() => {
      const currentRouteSignal = this.currentRouteSignal();
      if (currentRouteSignal) {
        this.routerSubscriptionHandler(currentRouteSignal);
      }
    });

    /**
     * Effect to handle changing the search term query param if the search term changes, particularly
     * for "Did You Mean" and "Search Instead" scenarios on the SRLP.
     */
    effect(async () => {
      const searchTerm = this.searchTerm();
      const queryParams = this.queryParams();
      const currentSearchTerm = queryParams?.['searchTerm'];

      if (currentSearchTerm && searchTerm && currentSearchTerm !== searchTerm) {
        await this.router.navigate([], {
          queryParams: { searchTerm },
          queryParamsHandling: 'merge',
        });
      }
    });

    effect(() => {
      this.store.set(
        'ENVIRONMENT_KILLSWITCH_VALUES',
        this.killSwitchService.killSwitchesFromEnvironment(),
      );
    });

    effect(() => {
      if (!this.compareCheckboxEnabled()) {
        return;
      }

      if (this.plpCompareService.shouldDrawerBeOpen()) {
        this.drawerComponent()?.openDrawer();
        this.hasDrawerBeenInitialized.set(true);
      } else if (this.hasDrawerBeenInitialized()) {
        this.drawerComponent()?.closeDrawer();
      }
    });

    const analyticsEvent = effect(() => {
      const plDataReady = this.isProductListReady();
      const analyticsReady = this.canProcessAnalytics();
      if (analyticsReady && plDataReady) {
        this.setFamilySRLPAnalyticsData();
        analyticsEvent.destroy();
      }
    });

    // Keep the store up to date when the store service updates the cookie value
    effect(() => {
      const storeCookieString = this.storeCookieValue();

      if (storeCookieString) {
        this.setStoreInfoFromCookieString(storeCookieString);
      }
    });
  }

  onDrawerClosed(): void {
    this.plpCompareService.closeCompareGrid();
  }

  private triggerAnalytics(CURRENT_QUERY_PARAMS) {
    // Get the current Date/Time
    const CURRENT_DATETIME = new Date();

    // Set the chain
    this.analyticsService.setChain(chain.chainName);

    // Set the Error ID (if present)
    this.analyticsService.setErrorId();

    // Set the Error Message (if present)
    this.analyticsService.setErrorMessage();

    // Set the referring URL
    this.analyticsService.setReferringPageUrl(this.document.referrer);

    // Set the server date
    this.analyticsService.setServerDate(this.analyticsService.getFormattedDate(CURRENT_DATETIME));

    // Set the server time
    this.analyticsService.setServerTime(this.analyticsService.getFormattedTime(CURRENT_DATETIME));

    // Set the base Search properties
    this.analyticsService.setSearchPageBaseProperties({
      searchDetails: {
        DYMTerm: CURRENT_QUERY_PARAMS.dymterm ?? CURRENT_QUERY_PARAMS.DYMTerm ?? '',
        DYMType: CURRENT_QUERY_PARAMS.didyoumean ?? CURRENT_QUERY_PARAMS.didYouMean ?? '',
        replacementTerm: CURRENT_QUERY_PARAMS.replacementTerm ?? '',
        searchTerm: CURRENT_QUERY_PARAMS.enteredSearchTerm ?? CURRENT_QUERY_PARAMS.searchTerm ?? '',
        searchType: CURRENT_QUERY_PARAMS.DSGsearchType ?? '',
      },
    });

    if (!this.canProcessAnalytics()) {
      const isFamilyPage = this.pageType() === 'plp';
      const pageType = isFamilyPage ? 'Family' : 'Search';
      this.analyticsService.setType('page', pageType);
      // Signal the effect to trigger setFamilySRLPAnalyticsData once page type is resolved
      this.canProcessAnalytics.set(true);
    }

    // Check to see if the `param` query parameter is present
    if (
      typeof CURRENT_QUERY_PARAMS.param === 'string' &&
      CURRENT_QUERY_PARAMS.param.trim() !== '' &&
      !this.cookieService.check('promoId')
    ) {
      // Set the promoId cookie
      this.cookieService.set('promoId', CURRENT_QUERY_PARAMS.param, '', '/');
    }
  }

  private routerSubscriptionHandler(route: {
    data: Observable<Data>;
    params: Observable<Params>;
    queryParams: Observable<Params>;
  }): void {
    // Get a Promise for route.data
    const dataPromise = firstValueFrom(route.data.pipe(take(1)));

    // Get a Promise for route.params
    const paramsPromise = firstValueFrom(route.params.pipe(take(1)));

    // Get a Promise for route.queryParams
    const queryParamsPromise = firstValueFrom(route.queryParams.pipe(take(1)));

    // Wait until all of the Promises have been resolved
    Promise.all([dataPromise, paramsPromise, queryParamsPromise]).then((data) => {
      // Get the current query parameters from the route query params
      const CURRENT_QUERY_PARAMS = data[2];

      // Only execute in the Browser
      if (isPlatformBrowser(this.platformId)) {
        // Do this early so we handle any recommendations that come in
        this.certonaService.bindRecommendationsHandler();

        this.utilityService.deferOnPrerendering(() => this.triggerAnalytics(CURRENT_QUERY_PARAMS));
        // Only execute on the Server
      } else {
        /*
         * Canonical link element
         */
        // Canonical URL
        const canonicalUrl = `${chain.metaCanonical}${this.router.url.split('?')[0]}`;

        this.createCanonicalTag(canonicalUrl);

        /*
         * OpenGraph url meta tag
         */
        this.metaService.addTag({ property: 'og:url', content: canonicalUrl }, true);
      }
    });
  }

  private setStoreInfoFromCookieString(cookieString: string): void {
    const dataFromStoreCookieString =
      this.storeService.getStoreDataFromStoreCookieString(cookieString);

    if (dataFromStoreCookieString?.storeZip || dataFromStoreCookieString?.storeNumber) {
      this.ngrxStore.dispatch(
        setUserStoreInfoFromCookie({
          zipcode: dataFromStoreCookieString.storeZip ?? '',
          location: dataFromStoreCookieString.storeNumber ?? '',
          name: dataFromStoreCookieString.storeName ?? '',
        }),
      );
      this.ngrxStore.dispatch(
        setUserStoreName({ storeName: dataFromStoreCookieString.storeName ?? '' }),
      );
    }
  }

  private setUserZipCodeFromCookie(): void {
    if (!this.sddZipCode()) {
      const sddZip =
        this.headerLocationService.getDeliveryZipCookie() ||
        this.headerLocationService.getWhereaboutsCookie();

      this.sddZipCode.set(sddZip);

      if (sddZip) {
        this.ngrxStore.dispatch(setUserZipCode({ zipCode: sddZip }));
      }
    }
  }

  createCanonicalTag(canonicalUrl: string): void {
    // Check to see canonical is already set for plp. To avoid duplicate canonical tag.
    if (this.pageType() === 'plp' && this.document.head.querySelector('[rel="canonical"]')) {
      return;
    }

    // Create a link element
    const canonicalLink = this.document.createElement('link');

    // Set the link relationship attribute
    canonicalLink.setAttribute('rel', 'canonical');

    // Set the link href attribute
    canonicalLink.setAttribute('href', canonicalUrl);

    // Append the link element to the head element
    this.document.head.appendChild(canonicalLink);
  }

  private setFamilySRLPAnalyticsData() {
    const plcData = this.productListCore();
    const mlSearchEventId = this.plpMlService.getMlSearchEventParentUUID();
    const productList = plcData?.productList;
    const isSearchRedirect = productList?.searchVO?.landingPageURL;

    /**
			If search page redirect to any other page, do not send reporting:PagePostRender.
		*/
    if (isSearchRedirect) {
      return;
    }

    if (productList?.didYouMeanVO) {
      this.window.siteData.DYMTerm = productList?.didYouMeanVO?.suggestions[0];
      this.window.siteData.DYMType = 'DYMAuto';
      this.window.siteData.EnteredSearchTerm = productList?.didYouMeanVO?.originalSearchTerm;
    }

    this.window.siteData.FamilyID = plcData.productListSEO?.id;
    this.window.siteData.FamilyName = plcData.productListSEO?.name;
    this.window.siteData.FuzzyMatch = productList?.fuzzyMatch;
    this.window.siteData.LoggedIn = undefined;
    this.window.siteData.MCSalesCatalogDetails = [{ code: plcData.productListSEO?.identifier }];
    this.window.siteData.MLBypass = !!productList?.mlBypass;
    this.window.siteData.MLGroup = productList?.mlGroup;
    this.window.siteData.MLRank = productList?.mlRank ?? '';
    this.window.siteData.MLRewrite = productList?.mlRewrite ?? '';
    this.window.siteData.MLSpelling = !!productList?.mlSpelling;
    this.window.siteData.PLP_event_id = mlSearchEventId;
    this.window.siteData.ProductCountTotal = productList?.totalCount;
    this.window.siteData.ProductPerPage = productList?.searchVO.pageSize;
    this.window.siteData.ProductsInFamily = productList?.totalCount;
    this.window.siteData.SearchData = productList?.searchVO.searchTypes;
    this.window.siteData.SearchResultCount = productList?.searchVO.totalCount.toString();
    this.window.siteData.SearchType = this.setSearchType(productList);
    this.window.siteData.SortMethod = analyticsSortMethodValues.find(
      (dropdownOption) => Number(plcData.selectedSort.value) === dropdownOption.value,
    )?.text;
    this.window.siteData.ReviewStarsProducts = productList?.productVOs?.filter(
      (product) => product.ratingCount > 0,
    ).length;

    this.window.siteData.ShipCount =
      productList?.fulfillmentFacets?.find((f) => f.name === 'SHIP')?.count || 0;
    this.window.siteData.PickupCount =
      productList?.fulfillmentFacets?.find((f) => f.name === 'BOPIS')?.count || 0;
    this.window.siteData.SameDayCount =
      productList?.fulfillmentFacets?.find((f) => f.name === 'SDD')?.count || 0;

    this.analyticsService.triggerAnalyticsEvent('reporting:PagePostRender');
  }

  setSearchType(productList: ProductList | null): string {
    const searchTypeDefault = 'Keyword';
    const isFamilyPage = this.pageType() === 'plp';
    const searchRefinement = this.cookieService.get('searchRefinement');

    if (productList?.totalCount === 0) {
      return '';
    }

    if (isFamilyPage) {
      const sTermCookie = this.cookieService.get('sTermCookie');
      if (sTermCookie) {
        this.window.siteData.EnteredSearchTerm = sTermCookie;
        this.cookieService.delete('sTermCookie', '/', '');
        return 'Redirect';
      }

      if (searchRefinement === 'categories') {
        return 'Suggested Category';
      } else {
        return '';
      }
    }

    if (!productList?.searchVO.landingPageURL && this.cookieService.get('sTermCookie')) {
      this.cookieService.delete('sTermCookie', '/', '');
    }

    // srlp
    if (searchRefinement?.includes('Recent')) {
      return 'Suggested History';
    }

    switch (searchRefinement) {
      case 'brands': {
        return 'Suggested Brand';
      }
      case 'enterSearchTerm': {
        return 'Keyword';
      }
      case 'searchTerm': {
        return 'Suggested Keyword';
      }
      case 'recentlySearchedTerm': {
        return 'Suggested Recently Searched';
      }
      default:
        return searchTypeDefault;
    }
  }

  /**
   * @description use the header service to get athlete's delivery zip code on page load
   */
  setDeliveryZipOnLoad(event) {
    if (event?.detail && event.detail !== this.sddZipCode()) {
      this.sddZipCode.set(event.detail);
      this.ngrxStore.dispatch(setUserZipCode({ zipCode: event.detail }));
    }
  }

  /**
   * @description use the header service to get athlete's delivery zip code on change
   */
  setDeliveryZipOnChange(event) {
    if (event?.detail && event.detail !== this.sddZipCode()) {
      this.sddZipCode.set(event.detail);
      this.ngrxStore.dispatch(setUserZipCode({ zipCode: event.detail }));
      this.ngrxStore.dispatch(productListLoadTriggered());
    }
  }

  /**
   * @description Listen for any changes in the header service to the store selection
   * if there is a change update the state
   */
  updateStoreDetails(event) {
    if (event?.detail) {
      this.ngrxStore.dispatch(changeHeaderServiceStoreInfo({ storeInfo: event.detail }));

      if (event.detail?.name) {
        this.ngrxStore.dispatch(setUserStoreName({ storeName: event.detail.name }));
      }
    }
  }
}
